import { useAnalytics } from '@src/stores-v2';
import { useTranslationStore } from '@src/stores-v2/StoreContext';
import { downloadTextFile, handleCopyToClipboard } from '@src/utility/general';
import {
  buildSrt,
  elementsToTimedWords,
  translationToTimedWords,
} from '@src/utility/srtExport';
import { needsSpaceBetweenPhrases } from '@src/utility/strings';
import { formatTranscriptElements } from '@src/utility/transcriptFormatUtils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type {
  TranslationData,
  TranslationPhrase,
} from '@src/types.ts/translation';

interface AnalyticsContext {
  storyId?: string;
  storyTitle?: string;
  videoId?: string;
  videoTitle?: string;
}

interface UseTranscriptCopyOptions {
  elements: TranscriptElement[] | undefined;
  getSpeakerName: (label: string | undefined) => string;
  analyticsContext: AnalyticsContext;
  /**
   * When set, the copy action emits the translated transcript for this
   * BCP 47 language instead of the source. The translated text is
   * assembled by replacing each source element's `value` with its
   * phrase's `translatedValue` (on the first index of the phrase, empty
   * elsewhere) before handing off to the shared paragraph formatter —
   * so speaker labels, timecodes, and paragraph grouping stay identical
   * to the source copy flow. Falls back to source text when no
   * translation is loaded for the language.
   */
  language?: string | null;
}

/**
 * Build a synthetic `TranscriptElement[]` whose `value`s are the
 * translated strings for elements covered by a phrase, empty for
 * mid-phrase continuation indices, and unchanged for indices outside
 * any phrase. Timestamps, speaker labels, and paragraph ids are
 * preserved so `formatTranscriptElements` groups identically to the
 * source flow — only the text differs.
 */
export function buildTranslatedElements(
  elements: TranscriptElement[],
  translation: TranslationData,
): TranscriptElement[] {
  const phraseByIndex = new Map<number, TranslationPhrase>();
  const firstIndexByPhrase = new Map<TranslationPhrase, number>();
  for (const phrase of translation.phrases) {
    // Retired fragments share their span with the override that replaced them,
    // so including one wins the per-index lookup and skews the separator.
    if (phrase.active === false) continue;
    let first = Number.POSITIVE_INFINITY;
    for (const idx of phrase.originalIndices) {
      phraseByIndex.set(idx, phrase);
      if (idx < first) first = idx;
    }
    firstIndexByPhrase.set(phrase, first);
  }
  // Each phrase's successor in source order, so the separator after a phrase
  // can be decided the way the inline views decide it (PLAT-11636: an
  // unconditional trailing space put an ASCII gap after every Japanese
  // phrase).
  const ordered = [...firstIndexByPhrase.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([phrase]) => phrase);
  const nextPhrase = new Map<TranslationPhrase, TranslationPhrase>();
  ordered.forEach((phrase, i) => {
    const next = ordered[i + 1];
    if (next) nextPhrase.set(phrase, next);
  });

  return elements.map(el => {
    const phrase = phraseByIndex.get(el.initial_index);
    if (!phrase) return el;
    const isFirst = el.initial_index === firstIndexByPhrase.get(phrase);
    if (!isFirst) return { ...el, value: '' };
    // A trailing space gives multi-sentence paragraphs natural "Sent1.
    // Sent2." spacing when the formatter concatenates elements within a
    // paragraph — only where the inline views would space the boundary too
    // (never inside CJK prose). The formatter trims paragraph-trailing
    // whitespace before emission, so a final space never leaks.
    const next = nextPhrase.get(phrase);
    const value = phrase.translatedValue ?? '';
    const separator =
      next && needsSpaceBetweenPhrases(value, next.translatedValue ?? '')
        ? ' '
        : '';
    return { ...el, value: `${value}${separator}` };
  });
}

/**
 * Copy / export logic for the transcript header (formerly the standalone
 * ExportTranscriptButton; PLAT-10646 moved the entry points into the
 * header's overflow menu). Handles translated-view assembly, the
 * `copy_transcription` analytics event, and SRT download.
 */
export function useTranscriptCopy({
  elements,
  getSpeakerName,
  analyticsContext,
  language,
}: UseTranscriptCopyOptions) {
  const analyticsStore = useAnalytics();
  const translationStore = useTranslationStore();
  const transactionId = useRef<string | null>(null);

  useEffect(() => {
    transactionId.current = uuid().substring(0, 8);
  }, []);

  // When a translated view is active AND the translation is loaded in
  // the store, swap the source text for the translated phrases before
  // handing off to the formatter. Everything else (speaker grouping,
  // timecodes, paragraph cadence) stays identical to the source flow.
  const effectiveElements = useMemo(() => {
    if (!elements || !language) return elements;
    const translation = translationStore.getTranslation(language);
    if (!translation) return elements;
    return buildTranslatedElements(elements, translation);
  }, [elements, language, translationStore, translationStore.translations]);

  const hasTranscript = useMemo(
    () =>
      elements?.some(
        el => !el.state || !['removed', 'cut'].includes(el.state),
      ) ?? false,
    [elements],
  );

  const copyTranscript = useCallback(
    (withSpeakers: boolean, withTimecodes: boolean) => {
      const formatted = formatTranscriptElements(effectiveElements, {
        withSpeakers,
        withTimecodes,
        getSpeakerName,
      });

      handleCopyToClipboard(formatted);

      const format =
        withSpeakers && withTimecodes
          ? 'speakers_and_timecodes'
          : withSpeakers
            ? 'speakers'
            : withTimecodes
              ? 'timecodes'
              : 'plain';

      analyticsStore.trackEvent({
        logMethod: 'info',
        eventName: 'copy_transcription',
        label: 'Copy transcript',
        transactionId: transactionId.current,
        extraProperties: {
          storyId: analyticsContext.storyId,
          storyTitle: analyticsContext.storyTitle,
          videoId: analyticsContext.videoId,
          videoTitle: analyticsContext.videoTitle,
          format,
          language: language ?? null,
        },
      });
    },
    [
      effectiveElements,
      getSpeakerName,
      analyticsStore,
      analyticsContext,
      language,
    ],
  );

  // Download the current view as an SRT file. Mirrors the copy flow's
  // view awareness: exports the translated phrases when a translated
  // language is active and loaded, otherwise the source words. Cues are
  // chunked to subtitle conventions (see `srtExport`), not paragraphs.
  const downloadSrt = useCallback(() => {
    if (!elements) return;
    const translation = language
      ? translationStore.getTranslation(language)
      : null;
    const words = translation
      ? translationToTimedWords(elements, translation)
      : elementsToTimedWords(elements);
    if (words.length === 0) return;

    const base =
      analyticsContext.videoTitle ||
      analyticsContext.storyTitle ||
      'transcript';
    const slug =
      base
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'transcript';
    const filename = `${slug}${language ? `-${language}` : ''}.srt`;
    downloadTextFile(filename, buildSrt(words), 'application/x-subrip');

    analyticsStore.trackEvent({
      logMethod: 'info',
      eventName: 'download_transcription',
      label: 'Download SRT',
      transactionId: transactionId.current,
      extraProperties: {
        storyId: analyticsContext.storyId,
        storyTitle: analyticsContext.storyTitle,
        videoId: analyticsContext.videoId,
        videoTitle: analyticsContext.videoTitle,
        format: 'srt',
        language: language ?? null,
      },
    });
  }, [elements, language, translationStore, analyticsStore, analyticsContext]);

  return { hasTranscript, copyTranscript, downloadSrt };
}
